var _excluded = ["as", "classes", "enabled", "onClose", "onClickOutside", "onEscapeKey", "initialFocusRef", "children", "autoFocus", "focusLockProps"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import classesPropType from "@xo-union/classes-prop-type";
import FocusLock from "react-focus-lock";
import noop from "lodash/noop";
import { DialogHeadingDescribeIDsProvider } from "../DialogHeading/index.js";
import { useModalRegisterEffect } from "../ModalRegister/index.js";
import useModalEffects from "../useModalEffects/index.js";

/**
 * A simple wrapper with some basic accessibility features for modals.
 *
 * ### Features
 *
 * 1. Disable scroll on document body
 * 2. Lock focus
 * 3. Keyboard accessibility (Closing on pressing ESC)
 * 4. Outside click handling
 * 5. Label association with nested `DialogHeading` elements
 */
var ModalContainer = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var Type = _ref.as,
    classes = _ref.classes,
    enabled = _ref.enabled,
    onClose = _ref.onClose,
    onClickOutside = _ref.onClickOutside,
    onEscapeKey = _ref.onEscapeKey,
    initialFocusRef = _ref.initialFocusRef,
    children = _ref.children,
    autoFocus = _ref.autoFocus,
    focusLockProps = _ref.focusLockProps,
    props = _objectWithoutProperties(_ref, _excluded);
  var dialogRef = React.useRef();
  React.useImperativeHandle(ref, function () {
    return dialogRef.current;
  });
  var activateFocusLock = React.useCallback(function () {
    var _initialFocusRef$curr;
    return initialFocusRef === null || initialFocusRef === void 0 ? void 0 : (_initialFocusRef$curr = initialFocusRef.current) === null || _initialFocusRef$curr === void 0 ? void 0 : _initialFocusRef$curr.focus();
  }, [initialFocusRef]);
  useModalEffects({
    onClose: onClose,
    onClickOutside: onClickOutside,
    onEscapeKey: onEscapeKey,
    enabled: enabled,
    classes: classes
  }, dialogRef);
  useModalRegisterEffect(enabled);
  var ariaProps = enabled ? {
    role: 'dialog',
    'aria-modal': 'true'
  } : {
    role: 'none',
    'aria-hidden': 'true'
  };
  return /*#__PURE__*/React.createElement(FocusLock, _extends({
    disabled: !enabled,
    autoFocus: initialFocusRef ? false : autoFocus,
    onActivation: initialFocusRef && activateFocusLock,
    returnFocus: true
  }, focusLockProps), /*#__PURE__*/React.createElement(DialogHeadingDescribeIDsProvider, null, function (headerDescribeIDs) {
    return /*#__PURE__*/React.createElement(Type, _extends({}, ariaProps, {
      ref: dialogRef,
      "aria-labelledby": headerDescribeIDs
    }, props), children);
  }));
});
ModalContainer.defaultProps = {
  autoFocus: true,
  enabled: true,
  as: 'div',
  onClose: noop,
  onClickOutside: noop,
  onEscapeKey: noop
};
process.env.NODE_ENV !== "production" ? ModalContainer.propTypes = {
  /**
   * Underlying element type for container
   */
  as: PropTypes.elementType,
  /**
   * Children to render
   */
  children: PropTypes.node,
  /**
   * Classes Dependencies
   */
  classes: classesPropType(['no-scroll']),
  /**
   * Enable/Disable accessibility features
   */
  enabled: PropTypes.bool,
  /**
   * Element to set as the initial focus
   */
  initialFocusRef: PropTypes.shape({
    current: PropTypes.shape({
      focus: PropTypes.func
    })
  }),
  /**
   * Callback to handle all events thaat trigger a modal close
   */
  onClose: PropTypes.func,
  /**
   * Callback to handle when clicking outside of the modal
   */
  onClickOutside: PropTypes.func,
  /**
   * Callback to handle when escape key is pressed
   */
  onEscapeKey: PropTypes.func,
  /**
   * Enable auto-focusing on the first focusable element. If `initialFocusRef` is given, this prop is ignored.
   */
  autoFocus: PropTypes.bool,
  /**
   * Passthrough props for the inner react-focus-lock FocusLock element.
   */
  focusLockProps: PropTypes.shape()
} : void 0;
ModalContainer.displayName = 'ModalContainer';
export default ModalContainer;